import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, inject, Injectable, } from '@angular/core';
import { Summary } from './summary';
import { AngularGridInstance, Column, GridService } from 'angular-slickgrid';
import { Operation } from './models/summary.types';
import { IOperationTracker } from './interfaces/IOperationTracker';
import { LocalizationService } from '@abp/ng.core';
import { localizeWithFallback } from '../utils/localization-utils';

@Injectable({
  providedIn: 'root',
})
export class SummaryService {
  private readonly localizationService = inject(LocalizationService);
  
  private summaryComponentRefs: Map<string, ComponentRef<Summary>> = new Map();
  private operationsTracker: Map<string, Operation> = new Map();

  private isVisible = false;
  private grid: AngularGridInstance;
  private gridId: string;
  private scrollListener?: () => void;

  constructor(
    private environmentInjector: EnvironmentInjector,
    private appRef: ApplicationRef
  ) {}

  initialize(instance: AngularGridInstance, id: string) {
    this.grid = instance;
    this.gridId = id;
    this.setupFooterScroll();
  }

  setTracker(tracker: IOperationTracker) {
    this.operationsTracker.set(tracker.columnId as string, tracker.operation);
  }

  toggle() {
    this.isVisible ? this.hide() : this.show();
  }

  show() {
    if (!this.gridId || !this.grid || this.isVisible) return;

    this.isVisible = true;
    this.updateFooterVisibility(true);
    this.insertFooterComponents();
  }

  hide() {
    if (!this.gridId || !this.isVisible) return;

    this.isVisible = false;
    this.updateFooterVisibility(false);
    this.clearFooterComponents(true);
  }

  handleColumnChange() {
    if (!this.isVisible || !this.gridId || !this.grid) return;

    const columns = this.grid.slickGrid.getColumns();
    this.insertFooterComponents();
    this.updateTrackedOperations();
  }

  handleSelectionChange() {
    if (!this.isVisible || !this.grid) return;

    this.updateFooterComponents();

    const selectedRows = this.grid.gridService.getSelectedRowsDataItem();
    if (selectedRows.length === 0) {
      this.hide();
    }
  }

  destroy() {
    // remove scroll listener
    if (this.scrollListener && this.gridId) {
      const viewport = document.querySelector(
        `#${this.gridId} .slick-viewport`
      ) as HTMLElement;

      if (viewport) {
        viewport.removeEventListener('scroll', this.scrollListener);
      }
      this.scrollListener = undefined;
    }

    if (this.gridId) {
      this.clearFooterComponents(true);
    }
    this.grid = null;
    this.gridId = null;
    this.isVisible = false;
  }

  private setupFooterScroll() {
    if (!this.gridId) return;

    const viewport = document.querySelector(
      `#${this.gridId} .slick-viewport`
    ) as HTMLElement;

    const footer = document.querySelector(
      `#${this.gridId} .slick-footerrow-columns`
    ) as HTMLElement;

    if (!viewport || !footer) return;

    // Sincronizar scroll horizontal del footer con el viewport
    this.scrollListener = () => {
      footer.style.transform = `translateX(${-viewport.scrollLeft}px)`;
    };

    viewport.addEventListener('scroll', this.scrollListener);
  }

  insertFooterComponents() {
    const footerColumns = this.getFooterColumnsFromDom();
    const selectedRows = this.grid.gridService.getSelectedRowsDataItem();
    const columnDefinitions = this.grid.slickGrid.getColumns();

    if (footerColumns.length === 0) {
      return;
    }

    this.clearFooterComponents(false);
    for (const [index, column] of columnDefinitions.entries()) {
      const footerCell = footerColumns[index] as HTMLElement;

      if (!footerCell) continue;

      footerCell.innerHTML = '';
      if (column.id === '_checkbox_selector') {
        footerCell.innerHTML = `<p>${localizeWithFallback(this.localizationService, 'LanguageService::TableSummary')}</p>`;
        continue;
      }

      if (column.id === '_edit' || column.id === '_read' || column.id === '_chevron') {
        footerCell.innerHTML = '';
        continue;
      }

      const componentContainer = document.createElement('div');
      componentContainer.style.width = '100%';
      componentContainer.style.height = '100%';
      componentContainer.style.display = 'flex';
      componentContainer.style.alignItems = 'center';
      footerCell.appendChild(componentContainer);

      const componentRef = createComponent(Summary, {
        environmentInjector: this.environmentInjector,
        hostElement: componentContainer,
      });
      componentRef.instance.columnId = column.id as string;
      componentRef.instance.columnField = column.params?.summaryField || column.field as string;
      componentRef.instance.columnType = this.mapFieldType(column.type);
      componentRef.instance.selectedRows = selectedRows;

      this.appRef.attachView(componentRef.hostView);
      componentRef.changeDetectorRef.detectChanges();
      this.summaryComponentRefs.set(column.id as string, componentRef);
    }
  }

  updateFooterComponents() {
    const selectedRows = this.grid.gridService.getSelectedRowsDataItem();

    this.summaryComponentRefs.forEach(componentRef => {
      componentRef.instance.updateSelectedRows(selectedRows);
      componentRef.changeDetectorRef.detectChanges();
    });
  }

  updateTrackedOperations() {
    for (const [columnId, componentRef] of this.summaryComponentRefs.entries()) {
      if (this.operationsTracker.has(columnId)) {
        const operation = this.operationsTracker.get(columnId);
        const operationOption = componentRef.instance.findOperationOption(operation);
        componentRef.instance.calculateTrakedOperation(operationOption);
        componentRef.changeDetectorRef.detectChanges();
      }
    }
  }

  clearFooterComponents(resetTracker?: boolean) {
    this.summaryComponentRefs.forEach(ref => {
      this.appRef.detachView(ref.hostView);
    });

    if (resetTracker) this.operationsTracker.clear();

    this.summaryComponentRefs.clear();
    const footerColumns = this.getFooterColumnsFromDom();
    footerColumns.forEach(col => (col.innerHTML = ''));
  }

  AddFooterComponentWhenSummaryActive() {
    if (this.isVisible) {
      this.insertFooterComponents();
      this.updateTrackedOperations();
    }
  }

  private getFooterColumnsFromDom(): NodeListOf<Element> {
    return document.querySelectorAll(
      `#${this.gridId} .slick-footerrow .slick-footerrow-column`
    );
  }

  private mapFieldType(fieldType?: string): 'number' | 'text' | 'date' | 'boolean' {
    switch (fieldType) {
      case 'number':
      case 'integer':
      case 'float':
      case 'decimal':
        return 'number';
      case 'date':
      case 'dateIso':
      case 'dateTime':
        return 'date';
      case 'boolean':
      case 'foreignKey':
        return 'boolean';
      default:
        return 'text';
    }
  }

  private updateFooterVisibility(visible: boolean) {
    if (!this.gridId) return;

    const footerRow = document.querySelector(
      `#${this.gridId} .slick-footerrow`
    ) as HTMLElement;

    if (footerRow) {
      if (visible) {
        footerRow.classList.add('visible-summary');
      } else {
        footerRow.classList.remove('visible-summary');
      }
    }
  }
}
